import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@material-ui/core';
import AppButton from 'components/AppButton';
import SectionHeading from 'components/SectionHeading';
import SectionSubtitle from 'components/SectionSubtitle';
import { OnlineRepairsCard } from './OnlineRepairsCard';
import titleUnderline from './0707.png';
import vectorImg from './Vector.png';
import styles from './OnlineRepairsSlider.module.css';

SwiperCore.use([Navigation]);

const CARDS = [ 2, 3, 4, 1, 5] as const;

function ChevronLeft() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export default function OnlineRepairsSlider() {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));
  const canLoop = CARDS.length > 1;

  const updateNavState = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <section className={styles.section} aria-labelledby="online-repairs-title">
      <img src={vectorImg} alt="" className={styles.decorImage} aria-hidden />
      <div className={styles.container}>
        <header className={styles.header}>
          <SectionHeading id="online-repairs-title" size="large" className={styles.title}>
            {t('Online Repairs')}
            <img src={titleUnderline} alt="" className={styles.titleUnderline} aria-hidden />
          </SectionHeading>
          <SectionSubtitle size="medium" className={styles.subtitle}>{t('Online Repairs subtitle')}</SectionSubtitle>
        </header>

        <div className={styles.sliderWrapper}>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              updateNavState(swiper);
            }}
            onSlideChange={(swiper) => updateNavState(swiper)}
            spaceBetween={24}
            slidesPerView={isMobile ? 1 : 3}
            slidesPerGroup={isMobile ? 1 : 1}
            loop={canLoop}
            className={styles.slider}
          >
            {CARDS.map((index) => (
              <SwiperSlide key={index} className={styles.slide}>
                <div className={styles.card}>
                  <OnlineRepairsCard cardIndex={index} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className={styles.navWrapper}>
          <AppButton
            color="secondary"
            variant="contained"
            type="button"
            className={styles.navButton}
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={!canLoop && isBeginning}
            aria-label="Previous slide"
          >
            <ChevronLeft />
          </AppButton>
          <AppButton
            color="secondary"
            variant="contained"
            type="button"
            className={styles.navButton}
            onClick={() => swiperRef.current?.slideNext()}
            disabled={!canLoop && isEnd}
            aria-label="Next slide"
          >
            <ChevronRight />
          </AppButton>
        </div>
      </div>
    </section>
  );
}
